<script lang="ts">
  import CardView from '../components/cards/CardView.svelte';
  import type { CardAnswer } from '../components/cards/answer';
  import { toasts } from '../components/toast-queue.svelte';
  import { CARD_TYPE_LABELS, type GradeResult } from '../components/types';
  import type { UseCasesFile } from '../lib/cards';
  import { documentTitle } from '../lib/router';
  import ConfirmDialog from './drill/ConfirmDialog.svelte';
  import { shouldShowUseCase, withResolvedUseCase } from './drill/display-card';
  import { isLocallyGraded, traceLocalGrade } from './drill/grade-telemetry';
  import { afterPaint } from './drill/paint';
  import { noticeMessage } from './drill/progress-view.svelte';
  import type { Session } from './drill/session.svelte';

  /**
   * ドリル（1 問 1 画面）。`#/session/:index` の index は 1 始まり。URL 直打ちのときだけ開く。
   * 採点とカード送りだけを出し、箱（Leitner）は渡さない（docs/decisions/problem-first.md 追記）。
   * 回答は session.record が進捗ストアに書く。Esc（何も開いていないとき）と「中断」は確認ダイアログを出す。
   */
  interface Props {
    session: Session;
    useCases: UseCasesFile | null;
    index: number;
    /** 中断の確認ダイアログを開いているか（Scoreboard の「中断」からも開く） */
    confirmingAbort: boolean;
    onnavigate: (hash: string, replace?: boolean) => void;
    /** 中断を求めた（Esc・中断ボタン）。確認ダイアログを開く */
    onabort: () => void;
    onconfirmabort: () => void;
    oncancelabort: () => void;
  }

  let {
    session,
    useCases,
    index,
    confirmingAbort,
    onnavigate,
    onabort,
    onconfirmabort,
    oncancelabort,
  }: Props = $props();

  const entry = $derived(session.entries[index - 1]);
  const total = $derived(session.total);
  const card = $derived(entry ? withResolvedUseCase(entry.card, useCases) : undefined);
  const showUseCase = $derived(
    entry
      ? shouldShowUseCase(entry.card, {
          fresh: entry.fresh,
          retry: entry.retry,
          answered: entry.result !== undefined,
        })
      : false,
  );

  $effect(() => {
    if (entry) {
      document.title = documentTitle(`${CARD_TYPE_LABELS[entry.card.type]} ${index} / ${total}`);
    }
  });

  function ongrade(result: GradeResult, answer: CardAnswer) {
    const current = entry;
    if (!current || current.result) return;
    const position = index - 1;
    const apply = () => {
      for (const n of session.record(position, result, answer)) toasts.show(noticeMessage(n));
    };
    if (isLocallyGraded(current.card, answer)) {
      void traceLocalGrade(current.card, result.verdict, apply, afterPaint);
    } else {
      apply();
    }
  }

  function onnext() {
    if (entry && !entry.result) session.skip(index - 1);
    if (index >= session.total) onnavigate('#/session/done');
    else onnavigate(`#/session/${index + 1}`);
  }

  function onprev() {
    if (index > 1) onnavigate(`#/session/${index - 1}`);
  }
</script>

<div class="page page-drill">
  {#if entry && card}
    {#key `${entry.card.id}:${index}`}
      <CardView
        globalKeys
        {card}
        {index}
        {total}
        result={entry.result}
        answer={entry.answer}
        {showUseCase}
        {ongrade}
        {onnext}
        {onprev}
        {onabort}
        onhint={() => session.markHint(index - 1)}
      />
    {/key}
  {/if}
</div>

<ConfirmDialog
  open={confirmingAbort}
  title="セッションを中断しますか"
  confirmLabel="中断する"
  cancelLabel="続ける"
  testid="abort-dialog"
  onconfirm={onconfirmabort}
  oncancel={oncancelabort}
>
  <p>ここまでの回答は保存されています。</p>
</ConfirmDialog>
